/* ConnectRealityPanel — feedback Real User Tribunal (Patrice A.).
 * Deux chemins d'adoption visibles très tôt :
 *   1. Utiliser mes données existantes (privilégié) — NOAM se greffe sur l'existant.
 *   2. Commencer manuellement (toujours possible).
 * N'affiche QUE des instruments réellement disponibles. Aucun connecteur inventé. */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plug, PenLine, ArrowRight, FileSpreadsheet, BookUser, FolderOpen, Info } from "lucide-react";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";

const REAL_INSTRUMENTS_DEFS = [
  {
    to: "/app/grc-matrix", icon: FileSpreadsheet,
    k: "cr_i1",
  },
  {
    to: "/app/annuaire?import=1", icon: BookUser,
    k: "cr_i2",
  },
  {
    to: "/app/documents", icon: FolderOpen,
    k: "cr_i3",
  },
];

const track = (event, meta) =>
  api.post("/workspace/agentic/track", { event, meta: meta || {} }).catch(() => {});

export default function ConnectRealityPanel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const REAL_INSTRUMENTS = REAL_INSTRUMENTS_DEFS.map((it) => ({ ...it, label: t(`saas.axp.${it.k}`), hint: t(`saas.axp.${it.k}h`) }));
  const [open, setOpen] = useState(false);
  const [desc, setDesc] = useState("");

  const openExisting = () => {
    if (!open) track("onboarding_path_existing", { surface: "agentic_home" });
    setOpen(!open);
  };

  const startManual = () => {
    track("onboarding_path_manual", { surface: "agentic_home" });
    navigate("/app/annuaire");
  };

  const describeSources = () => {
    const d = desc.trim();
    if (!d) return;
    track("source_mentioned", { surface: "connect_reality_panel" });
    const intent = t("saas.axp.cr_intent", { desc: d });
    navigate(`/app/intelligence?intent=${encodeURIComponent(intent)}`);
  };

  return (
    <div className="mt-5" data-testid="connect-reality-panel">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={openExisting} data-testid="connect-reality-existing-btn"
          className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition-colors ${open ? "border-[#1D9E75]/60 bg-[#1D9E75]/10 text-white" : "border-[#1D9E75]/40 bg-[#1D9E75]/[0.06] text-zinc-200 hover:border-[#1D9E75]/70 hover:text-white"}`}>
          <Plug className="w-4 h-4 text-[#1D9E75]" /> {t("saas.axp.cr_existing")}
        </button>
        <button onClick={startManual} data-testid="connect-reality-manual-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-white/10 bg-white/[0.02] text-sm text-zinc-400 hover:border-white/25 hover:text-white transition-colors">
          <PenLine className="w-4 h-4" /> {t("saas.axp.cr_manual")}
        </button>
        <span className="text-[11px] text-zinc-500">{t("saas.axp.cr_tagline")}</span>
      </div>

      {open && (
        <div className="mt-3 noam-card p-4 border-[#1D9E75]/25" data-testid="connect-reality-open">
          <div className="text-sm text-white font-medium">
            {t("saas.axp.cr_question")}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <input
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && describeSources()}
              placeholder={t("saas.axp.cr_placeholder")}
              data-testid="connect-reality-sources-input"
              className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#1D9E75]/60 placeholder:text-zinc-600"
            />
            <button onClick={describeSources} disabled={!desc.trim()}
              data-testid="connect-reality-sources-submit"
              className="btn-primary px-3 py-2 rounded-lg disabled:opacity-40">
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="text-[11px] text-zinc-500 mt-1.5">
            {t("saas.axp.cr_hint")}
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-2">
            {REAL_INSTRUMENTS.map((it) => (
              <button key={it.to} onClick={() => navigate(it.to)}
                data-testid={`connect-reality-instrument-${it.to.replace(/[^a-z-]+/g, "")}`}
                className="text-left rounded-lg border border-white/10 bg-white/[0.02] p-3 hover:border-[#1D9E75]/40 transition-colors">
                <div className="flex items-center gap-2 text-xs text-white font-medium">
                  <it.icon className="w-3.5 h-3.5 text-[#1D9E75]" /> {it.label}
                </div>
                <div className="text-[11px] text-zinc-500 mt-1 leading-snug">{it.hint}</div>
              </button>
            ))}
          </div>

          <div className="mt-3 flex items-start gap-1.5 text-[11px] text-zinc-400" data-testid="connect-reality-honesty-note">
            <Info className="w-3.5 h-3.5 shrink-0 mt-px text-zinc-500" />
            <span>
              {t("saas.axp.cr_note")}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
